import {
  type AccountInfo, BrowserAuthError, CacheLookupPolicy, InteractionRequiredAuthError, PublicClientApplication
} from '@azure/msal-browser';
import { BASE_SCOPES, CLIENT_ID, SIGN_IN_TENANT } from './config';
import { TimeoutError, withTimeout } from './timeout';

export { withTimeout } from './timeout';

/** Microsoft sign-in: any work or school account in production, one organization in test. */
export const msal: PublicClientApplication = new PublicClientApplication({
  auth: {
    clientId: CLIENT_ID,
    authority: `https://login.microsoftonline.com/${SIGN_IN_TENANT}`,
    redirectUri: window.location.origin
  },
  // Shared across tabs: a sign-in that returns in another tab (or after a browser restart)
  // still finds the request it belongs to.
  cache: { cacheLocation: 'localStorage' },
  // The quiet attempt in a hidden window gives up after 8 s (not 10) so a stuck one is noticed sooner.
  system: { iframeBridgeTimeout: 8000 }
});

/**
 * True until the app has finished loading once. While starting up nobody has clicked anything, so
 * when Microsoft needs the person we go there with a full-page redirect (never blocked), not a
 * popup (blocked or hidden without a click, which left the loading screen waiting forever).
 */
let startingUp: boolean = true;

export function startupDone(): void {
  startingUp = false;
}

/** One start-up redirect to Microsoft per 2 minutes, so a declined sign-in never loops. */
function redirectAllowed(): boolean {
  const key: string = 'aiadmin.startupRedirectAt';
  try {
    const last: number = Number(window.sessionStorage.getItem(key) ?? 0);
    if (Date.now() - last < 2 * 60 * 1000) {
      return false;
    }
    window.sessionStorage.setItem(key, String(Date.now()));
  } catch {
    // No storage: allow it.
  }
  return true;
}

/** MSAL's "a sign-in is in progress" marker, left behind when a sign-in window was interrupted. */
function clearStaleInteraction(): void {
  for (const store of [window.localStorage, window.sessionStorage]) {
    try {
      Object.keys(store).filter(k => /interaction\.status|interaction_status/i.test(k)).forEach(k => store.removeItem(k));
    } catch {
      // Storage not available: nothing to clear.
    }
  }
}

/**
 * Starts over in this browser: forgets the Microsoft sign-in and the app's own saved state, then
 * reloads. The person signs in again; nothing in their library or settings is touched.
 */
export function resetAndReload(): void {
  for (const store of [window.localStorage, window.sessionStorage]) {
    try {
      Object.keys(store).filter(k => /msal|login\.windows|login\.microsoftonline|aiadmin|organizer/i.test(k) || k.indexOf(CLIENT_ID) !== -1).forEach(k => store.removeItem(k));
    } catch {
      // Storage not available.
    }
  }
  window.location.replace(window.location.origin + window.location.pathname);
}



export async function initAuth(): Promise<AccountInfo | undefined> {
  await msal.initialize();
  const returning: boolean = /[#&?](code|error|state)=/.test(window.location.hash + window.location.search);
  // Finish a sign-in first. Coming back from Microsoft, MSAL may reload the page once and finish
  // on the second load from a note it left itself: clearing notes before this lost the sign-in.
  let result: Awaited<ReturnType<PublicClientApplication['handleRedirectPromise']>> = null;
  try {
    result = await msal.handleRedirectPromise();
  } catch (error) {
    // A stale or orphaned sign-in response (e.g. a refreshed #code=… address). Drop it and
    // carry on: the user is either still signed in, or simply sees the sign-in page again.
    console.warn('[SharePointOrganizer] Ignoring an incomplete sign-in response.', error);
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  }
  // Only now, with nothing left to finish, is a leftover "sign-in in progress" note stale (left by a
  // sign-in interrupted by a refresh or a new version). It would block every later sign-in.
  if (!result && !returning) {
    clearStaleInteraction();
  }
  const account: AccountInfo | undefined = result?.account ?? msal.getActiveAccount() ?? msal.getAllAccounts()[0];
  if (account) {
    msal.setActiveAccount(account);
  }
  return account;
}

export function signIn(): Promise<void> {
  return msal.loginRedirect({ scopes: BASE_SCOPES, prompt: 'select_account' });
}

export function signOut(): Promise<void> {
  return msal.logoutRedirect({ account: msal.getActiveAccount() ?? undefined });
}

/**
 * What to finish after coming back from Microsoft: the action the person clicked (e.g. "choose
 * the law pack"), saved for this tab only, so the app can complete it on its own.
 */
export interface IResume {
  kind: string;
  [key: string]: unknown;
}

const RESUME_KEY: string = 'aiadmin.resumeAfterSignIn';
let pendingResume: IResume | undefined;

/** Call just before a click's token request: if Microsoft needs the person, this action resumes on return. */
export function resumeAfterSignIn(action: IResume | undefined): void {
  pendingResume = action;
}

/** The action to finish after returning from Microsoft, if it's this kind (read once). */
export function takeResume(kind: string): IResume | undefined {
  try {
    const raw: string | null = window.sessionStorage.getItem(RESUME_KEY);
    const r: IResume | undefined = raw ? JSON.parse(raw) as IResume : undefined;
    if (r && r.kind === kind) {
      window.sessionStorage.removeItem(RESUME_KEY);
      return r;
    }
  } catch {
    // Nothing to resume.
  }
  return undefined;
}

/** Thrown when Microsoft needs the person and nobody clicked: the banner offers "Continue". */
export class SignInNeededError extends Error {
  constructor(public readonly scopes: string[]) {
    super('Microsoft needs you to sign in again. Press Continue at the top of the page.');
  }
}

/** Listeners for "Microsoft needs the person" (the app shows a banner with a Continue button). */
const needListeners: ((scopes: string[]) => void)[] = [];
export function onSignInNeeded(listener: (scopes: string[]) => void): () => void {
  needListeners.push(listener);
  return () => { needListeners.splice(needListeners.indexOf(listener), 1); };
}

/** Goes to Microsoft (full page, never a popup) and comes back to the same screen. */
export async function continueSignIn(scopes: string[], resume?: IResume): Promise<never> {
  const account: AccountInfo | null = msal.getActiveAccount();
  try {
    if (resume) {
      window.sessionStorage.setItem(RESUME_KEY, JSON.stringify(resume));
    }
  } catch {
    // The action just won't finish on its own; the person clicks it again.
  }
  clearStaleInteraction();
  await msal.acquireTokenRedirect({ scopes, account: account ?? undefined, redirectStartPage: window.location.href });
  return new Promise<never>(() => undefined); // The page is leaving.
}

/**
 * Access token for the given scopes. Never a popup: when Microsoft needs the person (consent,
 * signing in again, or a quiet attempt that was blocked or timed out), the app goes to Microsoft
 * with a full-page redirect and comes back to the same screen:
 *  - at start-up, or straight from a click (`fromClick`), right away; the clicked action resumes;
 *  - otherwise (background work), not by surprise: a banner asks the person to Continue.
 */
export async function getToken(scopes: string[], interactive: boolean = true, fromClick: boolean = false): Promise<string> {
  const account: AccountInfo | null = msal.getActiveAccount();
  if (!account) {
    throw new Error('You are signed out. Sign in again to continue.');
  }
  const resume: IResume | undefined = fromClick ? pendingResume : undefined;
  pendingResume = undefined;
  try {
    // From a click: only what's already stored, never the slow hidden window. Never longer than 15 s:
    // a quiet attempt that hangs counts as "needs the person".
    return (await withTimeout(
      msal.acquireTokenSilent({ scopes, account, ...(fromClick ? { cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken } : {}) }),
      15000, 'Microsoft took too long to answer.')).accessToken;
  } catch (error) {
    const person: boolean = fromClick || needsPerson(error) || error instanceof TimeoutError;
    if (!person || !interactive) {
      throw error;
    }
    if (fromClick || (startingUp && redirectAllowed())) {
      return continueSignIn(scopes, resume);
    }
    needListeners.forEach(l => l(scopes));
    throw new SignInNeededError(scopes);
  }
}

/**
 * Microsoft couldn't give a token without the person: consent or sign-in is needed, or the silent
 * attempt in a hidden window was blocked or timed out (browsers that block third-party cookies,
 * e.g. Safari, often end like that). Going to Microsoft's page and back settles it.
 */
export function needsPerson(error: unknown): boolean {
  return error instanceof InteractionRequiredAuthError
    || (error instanceof BrowserAuthError && /timed_out|monitor_window_timeout|block_iframe_reload|empty_window_error|no_tokens_found|no_account_error/.test(error.errorCode));
}
